import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { AxiosError } from 'axios';
import { User, LoginData, RegisterData } from '../types';
import { authAPI, setSessionInvalidatedCallback } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  sessionMessage: string | null;
  login: (data: LoginData) => Promise<void>;
  register: (data: RegisterData) => Promise<{ requires_approval?: boolean; message?: string } | void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearSessionMessage: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionMessage, setSessionMessage] = useState<string | null>(null);

  // Register the session-invalidation callback so the axios interceptor
  // can notify us when the backend returns SESSION_DISPLACED / SESSION_EXPIRED.
  useEffect(() => {
    setSessionInvalidatedCallback((_code, detail) => {
      setUser(null);
      localStorage.removeItem('wasAuthenticated');
      setSessionMessage(detail);
    });
    return () => setSessionInvalidatedCallback(null);
  }, []);

  const clearSessionMessage = useCallback(() => setSessionMessage(null), []);

  useEffect(() => {
    let cancelled = false;

    // Initialize CSRF token and check if user is already logged in
    const initializeAuth = async () => {
      // Try to get CSRF token first, but don't let it block auth check
      try {
        await authAPI.getCSRFToken();
      } catch (csrfError) {
        // CSRF token fetch is non-critical; session cookie is what matters
        if (process.env.NODE_ENV === 'development') console.log('CSRF token fetch failed (non-critical):', csrfError);
      }

      try {
        // Check if user is already logged in
        const response = await authAPI.getCurrentUser();
        if (cancelled) return;
        // Handle the case where response.data exists (user is authenticated)
        if (response.data) {
          setUser(response.data);
          localStorage.setItem('wasAuthenticated', 'true');
          if (process.env.NODE_ENV === 'development') console.log('User authenticated on init:', response.data.username);
        } else {
          // This shouldn't happen with 401 on unauthenticated, but handle it
          if (process.env.NODE_ENV === 'development') console.log('No authenticated user on init');
          setUser(null);
          localStorage.removeItem('wasAuthenticated');
        }
      } catch (error: unknown) {
        if (cancelled) return;
        const status = error instanceof AxiosError ? error.response?.status : undefined;
        // 401/403 mean not authenticated - expected when user is not logged in
        // DRF returns 403 "Authentication credentials were not provided" for unauthenticated requests
        if (status === 401 || status === 403) {
          if (process.env.NODE_ENV === 'development') console.log('Not authenticated on init'); // keep
          setUser(null);
          localStorage.removeItem('wasAuthenticated');
        } else {
          // Network error or server error — don't clear auth state if user was
          // previously authenticated; they may just have a transient connectivity issue.
          if (process.env.NODE_ENV === 'development') console.log('Error during auth init:', error); // keep
          const wasAuthenticated = localStorage.getItem('wasAuthenticated') === 'true';
          if (!wasAuthenticated) {
            setUser(null);
          }
          // If wasAuthenticated, keep user as null but don't redirect — retry once
          if (wasAuthenticated) {
            try {
              const retryResponse = await authAPI.getCurrentUser();
              if (cancelled) return;
              if (retryResponse.data) {
                setUser(retryResponse.data);
                if (process.env.NODE_ENV === 'development') console.log('User authenticated on retry:', retryResponse.data.username); // keep
              } else {
                setUser(null);
                localStorage.removeItem('wasAuthenticated');
              }
            } catch (retryError: unknown) {
              if (cancelled) return;
              const retryStatus = retryError instanceof AxiosError ? retryError.response?.status : undefined;
              if (retryStatus === 401 || retryStatus === 403) {
                setUser(null);
                localStorage.removeItem('wasAuthenticated');
              }
              // On persistent network error, still clear user to avoid stale state
              else {
                setUser(null);
              }
            }
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    initializeAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (data: LoginData) => {
    try {
      const response = await authAPI.login(data);
      setUser(response.data);
      localStorage.setItem('wasAuthenticated', 'true');

      // Don't verify with getCurrentUser immediately as it may cause logout
      // The session will be established after the login response
      if (process.env.NODE_ENV === 'development') console.log('Login successful, user set:', response.data.username);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (data: RegisterData) => {
    const response = await authAPI.register(data);
    const regData = response.data as User & { requires_approval?: boolean; message?: string };
    // Don't set user for managers - they need admin approval first, no session was created
    if (regData?.requires_approval) {
      return { requires_approval: true, message: regData.message };
    }
    setUser(response.data);
    localStorage.setItem('wasAuthenticated', 'true');
    // Verify session is established and refresh user data from server.
    // This mirrors the OAuth registration flow and ensures the session cookie
    // is recognised before subsequent authenticated requests (e.g. adding students).
    await refreshUser();
  };

  const logout = async () => {
    await authAPI.logout();
    setUser(null);
    localStorage.removeItem('wasAuthenticated');
  };

  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.getCurrentUser();
      if (response.data) {
        setUser(response.data);
        if (process.env.NODE_ENV === 'development') console.log('User data refreshed:', response.data.username);
      } else {
        setUser(null);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error refreshing user:', error); // keep
      const errStatus = error instanceof AxiosError ? error.response?.status : undefined;
      // 401/403 means session is truly gone — clear user to avoid stale state
      if (errStatus === 401 || errStatus === 403) {
        setUser(null);
        localStorage.removeItem('wasAuthenticated');
      }
      // Network/server errors: keep current user — likely transient
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, sessionMessage, login, register, logout, refreshUser, clearSessionMessage }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
